import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import "@/ember.css";
import { colors } from "@/theme";
import { Spinner } from "@/components/Controls";
import { logError } from "@/lib/log";
import { getStoredUserId, fetchUser, familyAccountGoogle } from "@/api";
import Landing from "@/components/Landing";
import Onboarding from "@/pages/Onboarding";
import VoiceSetup from "@/pages/VoiceSetup";
import Home from "@/pages/Home";
import WelcomeBack from "@/pages/WelcomeBack";
import Conversation from "@/pages/Conversation";
import History from "@/pages/History";
import Settings from "@/pages/Settings";
import Family from "@/pages/Family";
import FamilyAccount from "@/pages/FamilyAccount";
import FamilyPortal from "@/pages/FamilyPortal";
import PersonaSetup from "@/pages/PersonaSetup";
import Privacy from "@/pages/Privacy";

function hasGoogleCallback() {
  const raw = (window.location.hash || "") + (window.location.search || "");
  return /[?#&]session_id=/.test(raw);
}

function Index() {
  const navigate = useNavigate();
  const [processing] = useState(() => hasGoogleCallback());
  const [ready, setReady] = useState(!hasGoogleCallback());

  const enterApp = useCallback(async () => {
    const userId = getStoredUserId();
    if (!userId) {
      navigate("/onboarding");
      return;
    }
    try {
      const data = await fetchUser(userId);
      if (data.is_returning && data.memories.length > 0) navigate("/welcome-back");
      else navigate("/home");
    } catch {
      navigate("/home");
    }
  }, [navigate]);

  useEffect(() => {
    if (!processing) return;
    (async () => {
      const raw = (window.location.hash || "") + (window.location.search || "");
      const m = raw.match(/[?#&]session_id=([^&#]+)/);
      if (m) {
        const res = await familyAccountGoogle(decodeURIComponent(m[1]));
        try {
          const url = new URL(window.location.href);
          url.hash = "";
          url.searchParams.delete("session_id");
          window.history.replaceState(null, "", url.pathname + url.search);
        } catch (e) { logError("google-callback url cleanup", e); }
        if (res.ok) {
          navigate("/family-account", { replace: true });
          return;
        }
      }
      setReady(true);
    })();
  }, [processing, navigate]);

  if (processing && !ready) {
    return (
      <div data-testid="google-callback-loading" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: colors.surface }}>
        <Spinner size={44} />
      </div>
    );
  }
  return <Landing onEnter={enterApp} />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/voice-setup" element={<VoiceSetup />} />
        <Route path="/home" element={<Home />} />
        <Route path="/welcome-back" element={<WelcomeBack />} />
        <Route path="/conversation" element={<Conversation />} />
        <Route path="/history" element={<History />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/family" element={<Family />} />
        <Route path="/family-account" element={<FamilyAccount />} />
        <Route path="/family-portal" element={<FamilyPortal />} />
        <Route path="/persona-setup" element={<PersonaSetup />} />
        <Route path="/privacy" element={<Privacy />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
